import { ExportOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Flex, type MenuProps, Tooltip } from "antd";
import React from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import ActiveRecordingView from "../../components/ActiveRecordingView/ActiveRecordingView.component";
import ChangePassword from "../../components/ChangePassword/ChangePassword.component";
import ClientView from "../../components/ClientView/ClientView.component";
import { TabType, ViewType } from "../../components/CreateNoteModal/AITemplateModal/types";
import NudgeLogo from "../../components/NudgeLogo";
import { isFromSOL } from "../../domain/groups.domain";
import { isLockedOutOfApp } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import useIntercomWithData from "../../hooks/useIntercomWithData";
import {
  setAITemplateModalState,
  setCreateModalState,
  setCustomTemplateSelectedTab,
  setEditingState,
  setNotesViewVisible,
  setOpenShare,
  setSelectedClient,
  setSelectedView,
  setShowSettingsModal,
  setSidebarCollapsed,
} from "../../slices/appStateSlice";
import { UserTier, resetBasicUserInfo } from "../../slices/authSlice";
import type { AppDispatch } from "../../store";
import { EventType, trackEvent, trackServerEvent } from "../../utils/analytics";
import { clearSessionAndRedirect } from "../../utils/auth.utils";
import { STRIPE_BILLING_PORTAL_URL } from "../../utils/constants";
import styles from "./home.module.scss";

const componentName = "home";

interface SidebarProps {
  isPaused?: boolean;
  recordingTime?: number;
  handlePauseOrEndRecording?: () => void;
  handleResumeRecording?: () => void;
  handleSubmitAudio?: () => void;
  cancelRecording?: () => void;
  showClientList?: boolean;
}

const noop = () => {};

const Sidebar: React.FC<SidebarProps> = ({
  isPaused = false,
  recordingTime = 0,
  handlePauseOrEndRecording = noop,
  handleResumeRecording = noop,
  handleSubmitAudio = noop,
  cancelRecording = noop,
  showClientList = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { show: showIntercom } = useIntercomWithData();

  const { sidebarCollapsed, showRecordingView } = useAppSelector((state) => state.appState);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const userData = useAppSelector((state) => state.user.data);

  const isLocked = isLockedOutOfApp(userInfo);
  const [openChangePasswordModal, setOpenChangePasswordModal] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // --- Navigation ---
  const navigateToHome = () => {
    navigate("/home");
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setSelectedView(ViewType.DASHBOARD));
  };

  const openTemplateGallery = () => {
    dispatch(setSelectedView(ViewType.TEMPLATE));
    navigate("/templates");
  };

  const handleStartSession = () => {
    if (!location.pathname.startsWith("/home")) {
      navigate("/home");
    }
    dispatch(setEditingState(false));
    dispatch(setCreateModalState(true));
  };

  const openMyTemplates = () => {
    if (!location.pathname.startsWith("/home")) {
      navigate("/home");
    }
    dispatch(setAITemplateModalState(true));
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
  };

  const openShare = () => {
    trackEvent(EventType.SHARE_MODAL_OPEN, { shareModalType: "share_button" });
    trackServerEvent(EventType.SHARE_MODAL_OPEN, { shareModalType: "share_button" });
    dispatch(setOpenShare(true));
  };

  const contactUs = () => {
    trackEvent(EventType.CONTACT_US);
    if (isFromSOL(userInfo)) {
      window.open("https://tally.so/r/3jLbZa", "_blank");
    } else {
      window.open("https://tally.so/r/wg5QxK", "_blank");
    }
  };

  const logout = async () => {
    trackEvent(EventType.LOGOUT);
    await axiosInstance.post("/rest-auth/logout/", {}).catch((error) => {
      console.error(error);
    });
    dispatch(resetBasicUserInfo());
    clearSessionAndRedirect();
  };

  // --- Active state helpers ---
  const isDashboardActive =
    location.pathname === "/home" || location.pathname === "/" || location.pathname.startsWith("/home/");
  const isTemplateGalleryActive = location.pathname.startsWith("/templates");

  // --- Menu items ---
  const sidebarMenuItems: MenuProps["items"] = [
    {
      key: "settings",
      label: (
        <div style={{ color: "#000" }} onClick={() => dispatch(setShowSettingsModal(true))}>
          Settings
        </div>
      ),
    },
    ...(userInfo.is_paying || (userInfo.tier === UserTier.GROUP && userInfo.is_group_admin)
      ? [
          {
            key: "my-account",
            label: (
              <div onClick={() => window.open(STRIPE_BILLING_PORTAL_URL, "_blank")} style={{ color: "#000" }}>
                My Account
              </div>
            ),
          },
        ]
      : []),
    {
      key: "templates",
      label: (
        <div onClick={openTemplateGallery} style={{ color: "#000" }}>
          Template Gallery
        </div>
      ),
    },
    {
      key: "ai-receptionist",
      label: (
        <div
          onClick={() => {
            dispatch(setSelectedView(ViewType.AI_RECEPTIONIST));
            navigate("/call/dashboard");
          }}
          style={{ color: "#000" }}
        >
          AI Call Receptionist
        </div>
      ),
    },
    ...(!userInfo.is_paying || userInfo.group_name
      ? [
          {
            key: "support",
            label: (
              <div onClick={() => showIntercom()} style={{ color: "#000" }}>
                Support
              </div>
            ),
          },
        ]
      : []),
    {
      key: "new-features",
      label: (
        <a href="https://getnudgeai.com/release-notes" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          New Features <ExportOutlined />
        </a>
      ),
    },
    {
      key: "contact",
      label: (
        <div onClick={contactUs} style={{ color: "#000" }}>
          Contact Us
        </div>
      ),
    },
    {
      key: "share",
      label: (
        <div onClick={openShare} style={{ color: "#000" }}>
          Share & Earn Free a Month
        </div>
      ),
    },
    {
      key: "consent-form",
      label: (
        <a href="/pdf/client_consent_form.pdf" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Consent Form <ExportOutlined />
        </a>
      ),
    },
    {
      key: "tutorial",
      label: (
        <a
          href={
            isFromSOL(userInfo)
              ? "https://www.youtube.com/playlist?list=PL9lb9UfUiaJwSF0u5fBx22xb_CwFExyKu"
              : "https://www.youtube.com/playlist?list=PL9lb9UfUiaJwUUCOBLgiznYWj8NT-7jD2"
          }
          target="_blank"
          style={{ color: "#000" }}
          rel="noreferrer"
        >
          Tutorial <ExportOutlined />
        </a>
      ),
    },
    {
      key: "ambassador",
      label: (
        <a href="https://tally.so/r/3xNd0G" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Become an Influencer <ExportOutlined />
        </a>
      ),
    },
    {
      key: "change-password",
      label: (
        <div onClick={() => setOpenChangePasswordModal(true)} style={{ color: "#000" }}>
          Change Password
        </div>
      ),
    },
    {
      key: "logout",
      label: (
        <div onClick={logout} style={{ color: "red" }}>
          Logout
        </div>
      ),
    },
  ];

  // --- Icons (16px) ---
  const HomeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.5 7.5L10 1.667 17.5 7.5V16.667a1.667 1.667 0 01-1.667 1.666H4.167A1.667 1.667 0 012.5 16.667V7.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 18.333V10h5v8.333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const TemplateGalleryIcon = () => (
    <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.83333 8.33333H0.833333C0.61232 8.33333 0.400358 8.42113 0.244078 8.57741C0.0877973 8.73369 0 8.94565 0 9.16667V14.1667C0 14.3877 0.0877973 14.5996 0.244078 14.7559C0.400358 14.9122 0.61232 15 0.833333 15H5.83333C6.05435 15 6.26631 14.9122 6.42259 14.7559C6.57887 14.5996 6.66667 14.3877 6.66667 14.1667V9.16667C6.66667 8.94565 6.57887 8.73369 6.42259 8.57741C6.26631 8.42113 6.05435 8.33333 5.83333 8.33333ZM5 13.3333H1.66667V10H5V13.3333ZM14.1667 0H9.16667C8.94565 0 8.73369 0.0877973 8.57741 0.244078C8.42113 0.400358 8.33333 0.61232 8.33333 0.833333V5.83333C8.33333 6.05435 8.42113 6.26631 8.57741 6.42259C8.73369 6.57887 8.94565 6.66667 9.16667 6.66667H14.1667C14.3877 6.66667 14.5996 6.57887 14.7559 6.42259C14.9122 6.26631 15 6.05435 15 5.83333V0.833333C15 0.61232 14.9122 0.400358 14.7559 0.244078C14.5996 0.0877973 14.3877 0 14.1667 0V0ZM13.3333 5H10V1.66667H13.3333V5ZM14.1667 10.8333H12.5V9.16667C12.5 8.94565 12.4122 8.73369 12.2559 8.57741C12.0996 8.42113 11.8877 8.33333 11.6667 8.33333C11.4457 8.33333 11.2337 8.42113 11.0774 8.57741C10.9211 8.73369 10.8333 8.94565 10.8333 9.16667V10.8333H9.16667C8.94565 10.8333 8.73369 10.9211 8.57741 11.0774C8.42113 11.2337 8.33333 11.4457 8.33333 11.6667C8.33333 11.8877 8.42113 12.0996 8.57741 12.2559C8.73369 12.4122 8.94565 12.5 9.16667 12.5H10.8333V14.1667C10.8333 14.3877 10.9211 14.5996 11.0774 14.7559C11.2337 14.9122 11.4457 15 11.6667 15C11.8877 15 12.0996 14.9122 12.2559 14.7559C12.4122 14.5996 12.5 14.3877 12.5 14.1667V12.5H14.1667C14.3877 12.5 14.5996 12.4122 14.7559 12.2559C14.9122 12.0996 15 11.8877 15 11.6667C15 11.4457 14.9122 11.2337 14.7559 11.0774C14.5996 10.9211 14.3877 10.8333 14.1667 10.8333ZM5.83333 0H0.833333C0.61232 0 0.400358 0.0877973 0.244078 0.244078C0.0877973 0.400358 0 0.61232 0 0.833333V5.83333C0 6.05435 0.0877973 6.26631 0.244078 6.42259C0.400358 6.57887 0.61232 6.66667 0.833333 6.66667H5.83333C6.05435 6.66667 6.26631 6.57887 6.42259 6.42259C6.57887 6.26631 6.66667 6.05435 6.66667 5.83333V0.833333C6.66667 0.61232 6.57887 0.400358 6.42259 0.244078C6.26631 0.0877973 6.05435 0 5.83333 0V0ZM5 5H1.66667V1.66667H5V5Z"
        fill="currentColor"
      />
    </svg>
  );

  const FolderIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.667 17.5H3.333A1.667 1.667 0 011.667 15.833V4.167A1.667 1.667 0 013.333 2.5h4.167l2.5 2.5h6.667a1.667 1.667 0 011.666 1.667v9.166A1.667 1.667 0 0116.667 17.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CollapseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1.5" y="2.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="1.5" y="2.5" width="5.5" height="15" rx="1" fill="currentColor" opacity="0.4" />
      <line x1="7" y1="2.5" x2="7" y2="17.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );

  const ExpandIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1.5" y="2.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="1.5" y="2.5" width="5.5" height="15" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );

  const ChevronIcon = ({ up = false }: { up?: boolean }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transition: "transform 0.2s ease", transform: up ? "rotate(0deg)" : "rotate(180deg)" }}
    >
      <path d="M4 10L8 6l4 4" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      <div
        className={`${styles[`${componentName}__container-sidebar`]} ${sidebarCollapsed ? styles[`${componentName}__container-sidebar--collapsed`] : ""}`}
      >
        {/* Collapsed sidebar */}
        <div className={styles[`${componentName}__container-sidebar-collapsed-content`]}>
          <Flex vertical align="center" gap={12}>
            <Tooltip title="Expand Sidebar" placement="right">
              <Button
                type="text"
                className={styles[`${componentName}__container-side-collapsed-icon`]}
                icon={<ExpandIcon />}
                onClick={() => dispatch(setSidebarCollapsed(false))}
              />
            </Tooltip>
            <Tooltip title="Nudge AI" placement="right">
              <div
                className={styles[`${componentName}__container-side-collapsed-logo`]}
                onClick={navigateToHome}
                style={{ cursor: "pointer", fontWeight: 800, fontSize: 22, color: "#6366F1" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigateToHome()}
              >
                n
              </div>
            </Tooltip>
            {!showRecordingView && (
              <>
                <Tooltip title="Your Dashboard" placement="right">
                  <Button
                    type="text"
                    className={`${styles[`${componentName}__container-side-collapsed-icon`]} ${isDashboardActive ? styles[`${componentName}__container-side-collapsed-icon--active`] : ""}`}
                    onClick={navigateToHome}
                    icon={<HomeIcon />}
                  />
                </Tooltip>
                <Tooltip title="Template Gallery" placement="right">
                  <Button
                    type="text"
                    className={`${styles[`${componentName}__container-side-collapsed-icon`]} ${isTemplateGalleryActive ? styles[`${componentName}__container-side-collapsed-icon--active`] : ""}`}
                    onClick={openTemplateGallery}
                    icon={<TemplateGalleryIcon />}
                  />
                </Tooltip>
              </>
            )}
            {showRecordingView ? (
              <Tooltip title="Recording in progress — click to expand" placement="right">
                <Button
                  type="text"
                  className={styles[`${componentName}__container-side-collapsed-mic-btn`]}
                  onClick={() => dispatch(setSidebarCollapsed(false))}
                  icon={
                    <span className={styles[`${componentName}__container-side-collapsed-mic`]}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 1a4 4 0 00-4 4v7a4 4 0 008 0V5a4 4 0 00-4-4z" fill="white" />
                        <path
                          d="M19 10v2a7 7 0 01-14 0v-2"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <line x1="12" y1="19" x2="12" y2="23" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                  }
                />
              </Tooltip>
            ) : (
              <Tooltip title="Start a Session" placement="right">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined style={{ fontSize: 12 }} />}
                  className={styles[`${componentName}__container-side-collapsed-add`]}
                  onClick={handleStartSession}
                />
              </Tooltip>
            )}
            {!showRecordingView && (
              <Tooltip title="My Templates" placement="right">
                <Button
                  type="text"
                  className={styles[`${componentName}__container-side-collapsed-icon`]}
                  onClick={openMyTemplates}
                  icon={<FolderIcon />}
                />
              </Tooltip>
            )}
          </Flex>
          <Flex vertical align="center" style={isLocked ? { position: "relative", zIndex: 1101 } : undefined}>
            <Dropdown
              menu={{ items: sidebarMenuItems }}
              placement="topLeft"
              trigger={["click"]}
              overlayStyle={{ zIndex: 1101 }}
            >
              <Tooltip title="Settings" placement="right">
                <Button
                  type="text"
                  icon={<SettingOutlined style={{ fontSize: 16 }} />}
                  className={styles[`${componentName}__container-side-collapsed-gear`]}
                />
              </Tooltip>
            </Dropdown>
          </Flex>
        </div>

        {/* Expanded sidebar */}
        <div className={styles[`${componentName}__container-sidebar-expanded-content`]}>
          <div className={styles[`${componentName}__container-sidebar-header`]}>
            <div className={styles[`${componentName}__container-sidebar-header-logo-box`]}>
              <NudgeLogo practiceArea={userInfo.practice_area} onClick={navigateToHome} fontSize={16} />
              <Button
                type="text"
                className={styles[`${componentName}__container-sidebar-toggle`]}
                icon={<CollapseIcon />}
                onClick={() => dispatch(setSidebarCollapsed(true))}
              />
            </div>
          </div>

          {showRecordingView && (
            <Flex vertical align="center" className={styles[`${componentName}__container-sidebar-recording-padding`]}>
              <ActiveRecordingView
                isPaused={isPaused}
                recordingTime={recordingTime}
                handlePauseOrEndRecording={handlePauseOrEndRecording}
                handleResumeRecording={handleResumeRecording}
                handleSubmitAudio={handleSubmitAudio}
                cancelRecording={cancelRecording}
              />
            </Flex>
          )}

          {!showRecordingView && (
            <nav className={styles[`${componentName}__container-sidebar-nav`]}>
              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${isDashboardActive ? styles[`${componentName}__container-sidebar-nav-item--active`] : ""}`}
                onClick={navigateToHome}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigateToHome()}
              >
                <HomeIcon />
                <span>Your Dashboard</span>
              </div>
              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${isTemplateGalleryActive ? styles[`${componentName}__container-sidebar-nav-item--active`] : ""}`}
                onClick={openTemplateGallery}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openTemplateGallery()}
              >
                <TemplateGalleryIcon />
                <span>Template Gallery</span>
              </div>

              <div
                className={`${styles[`${componentName}__container-sidebar-nav-item`]} ${styles[`${componentName}__container-sidebar-start-session`]}`}
                onClick={handleStartSession}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleStartSession()}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="8" fill="#6366F1" />
                  <path d="M8 4.5V11.5M4.5 8H11.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>Start a Session</span>
              </div>
              <div
                className={styles[`${componentName}__container-sidebar-nav-item`]}
                onClick={openMyTemplates}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openMyTemplates()}
              >
                <FolderIcon />
                <span>My Templates</span>
              </div>
            </nav>
          )}

          <div className={styles[`${componentName}__container-sidebar-body`]}>
            {showClientList && (
              <Flex style={{ flex: 1, minHeight: 0 }}>
                <ClientView />
              </Flex>
            )}
          </div>

          <div
            className={styles[`${componentName}__container-sidebar-footer`]}
            style={isLocked ? { position: "relative", zIndex: 1101 } : undefined}
          >
            <Dropdown
              menu={{ items: sidebarMenuItems, style: { padding: 4 } }}
              placement="topRight"
              trigger={["click"]}
              overlayStyle={{ zIndex: 1101, minWidth: "auto" }}
              onOpenChange={setDropdownOpen}
              open={dropdownOpen}
            >
              <Flex align="center" gap={10} className={styles[`${componentName}__container-sidebar-footer-profile`]}>
                {userInfo.group_logo ? (
                  <img
                    height={28}
                    width="auto"
                    src={userInfo.group_logo as string}
                    alt={userInfo.group_name || ""}
                    style={{ borderRadius: 4, flexShrink: 0, border: "1px solid #E5E7EB" }}
                  />
                ) : (
                  <Avatar
                    size={32}
                    style={{
                      backgroundColor: "#6366F1",
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {(userData?.name?.[0] || userData?.name?.[0] || "U").toUpperCase()}
                  </Avatar>
                )}
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <span className={styles[`${componentName}__container-sidebar-footer-name`]}>
                    {userData?.name ? `${userData.name || ""}`.trim() : userData?.email || "User"}
                  </span>
                  <span className={styles[`${componentName}__container-sidebar-footer-email`]}>
                    {userData?.email || ""}
                  </span>
                </Flex>
                <span className={styles[`${componentName}__container-sidebar-footer-chevron`]}>
                  <ChevronIcon up={dropdownOpen} />
                </span>
              </Flex>
            </Dropdown>
          </div>
        </div>
      </div>

      {openChangePasswordModal && (
        <ChangePassword show={openChangePasswordModal} onClose={() => setOpenChangePasswordModal(false)} />
      )}
    </>
  );
};

export default Sidebar;
